import { useState } from "react";
import type { VerifyResult } from "../core/index.ts";
import { Modal } from "./forms/Modal.tsx";
import { formatPairId } from "./pairing.ts";

const PRO_FEATURES = [
  "Unlimited vehicles",
  "Fuel, charge & expense logging",
  "Category ledger & totals",
  "On-device assistant",
  "Local backup (export / import)",
  "Trends, charts & breakdowns",
  "Tires, maintenance, trips & valuation",
  "Photo & document attachments",
  "Cloud AI assistant",
  "Home-screen widget",
  "GPS trip recording (auto via Bluetooth)",
  "Route map, speed playback & find my car",
  "Scan & auto-fill from receipts",
  "Maintenance catalog (60+ procedures)",
  "CSV & PDF vehicle reports",
];

const REASON: Record<string, string> = {
  format: "That doesn't look like a valid unlock code.",
  signature: "This code isn't valid for this app.",
  expired: "This code has expired — open a fresh one in the app.",
  tier: "This code doesn't grant Pro.",
  device: "This code was generated for a different browser. In the app, enter the pairing code shown below, then paste the new code here.",
};

export function Paywall({ onClose, onRedeem, pairId }: { onClose: () => void; onRedeem: (code: string) => Promise<VerifyResult>; pairId?: string }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit() {
    setErr(null);
    setBusy(true);
    const r = await onRedeem(code);
    setBusy(false);
    if (r.ok) onClose();
    else setErr(REASON[r.reason ?? "format"] ?? "Could not verify that code.");
  }

  return (
    <Modal title="CarSpend · Pro" onClose={onClose}>
      <div style={{ fontFamily: "var(--display)", fontWeight: 900, fontSize: 40, lineHeight: 0.95 }}>
        Go <span style={{ color: "var(--amber)" }}>pro.</span>
      </div>
      <div className="panel" style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
        <span className="mono amber" style={{ fontSize: 10, letterSpacing: 1.6 }}>HOW IT WORKS</span>
        <p className="mono muted" style={{ fontSize: 11, lineHeight: 1.6, margin: 0 }}>
          <span className="cream">1.</span> Subscribe in the CarSpend app (Google Play / App Store), if you haven't already.<br />
          <span className="cream">2.</span> Copy your <span className="amber">browser pairing code</span> from step 1 below.<br />
          <span className="cream">3.</span> On the app's <span className="amber">Pro screen</span>, paste that pairing code and tap <span className="amber">Generate</span> — it creates an unlock code tied to this browser.<br />
          <span className="cream">4.</span> Copy the unlock code from the app, paste it into step 2 below, and tap <span className="amber">Unlock</span>.
        </p>
        <p className="mono muted" style={{ fontSize: 10, lineHeight: 1.6, margin: 0 }}>
          <span className="amber">For security, an unlock code works on one browser at a time.</span> It's tied to this
          exact browser's pairing code, and only the most recently unlocked browser stays active — unlocking a
          different browser later automatically signs this one out. This is what stops codes from being shared or resold,
          and it's why unlocking needs a quick internet connection to check.
        </p>
      </div>

      {pairId && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: 1.4, color: "var(--muted)", textTransform: "uppercase", display: "block" }}>
            1 · Your browser pairing code
          </span>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10, padding: "10px 12px" }}>
            <span className="mono amber" style={{ fontSize: 18, letterSpacing: 3 }}>{formatPairId(pairId)}</span>
            <button
              className="btn ghost"
              style={{ fontSize: 10, padding: "6px 12px" }}
              onClick={() => { void navigator.clipboard?.writeText(formatPairId(pairId)); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            >
              {copied ? "COPIED" : "COPY"}
            </button>
          </div>
          <span className="mono muted" style={{ fontSize: 9, lineHeight: 1.5 }}>
            Enter this in the CarSpend app's Pro screen — it ties the unlock code to this browser.
          </span>
        </div>
      )}

      <label style={{ display: "block", marginTop: 6 }}>
        <span style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: 1.4, color: "var(--muted)", textTransform: "uppercase", display: "block", marginBottom: 5 }}>
          2 · Unlock code
        </span>
        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="CSL1.…"
          rows={3}
          style={{ width: "100%", background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10, color: "var(--cream)", fontFamily: "var(--mono)", fontSize: 12, padding: 12, resize: "vertical" }}
        />
      </label>
      {err && <div className="mono" style={{ color: "var(--redline)", fontSize: 12 }}>{err}</div>}

      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
        <div style={{ flex: 1 }} />
        <button className="btn primary" disabled={busy || code.trim() === ""} style={{ opacity: busy || !code.trim() ? 0.5 : 1 }} onClick={submit}>
          {busy ? "CHECKING…" : "UNLOCK"}
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 28, borderTop: "1px solid var(--edge)", paddingTop: 22 }}>
        <span className="mono amber" style={{ fontSize: 10, letterSpacing: 1.6 }}>EVERYTHING IN PRO</span>
        <ul className="cs-feats">
          {PRO_FEATURES.map((f) => (
            <li key={f} className="mono" style={{ fontSize: 13, color: "var(--cream)" }}>
              <span className="lime">✓</span> {f}
            </li>
          ))}
        </ul>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
          <p className="mono" style={{ color: "var(--creamDim)", fontSize: 12, lineHeight: 1.6, margin: 0 }}>
            Don't have the app yet? Get CarSpend on your phone — Pro is a subscription there.
          </p>
          <div className="cs-stores">
            <a className="cs-store" href="https://play.google.com/store/apps/details?id=com.carspend.android" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.6 2.3c-.3.3-.5.7-.5 1.3v16.8c0 .6.2 1 .5 1.3l.1.1L13 12.1v-.2L3.7 2.2l-.1.1zM16.8 15.3l-3.1-3.1v-.2l3.1-3.1.1.1 3.7 2.1c1 .6 1 1.6 0 2.2l-3.7 2.1-.1-.1zM13.7 11.9l3.1 3.1L5.9 21.4c-.4.2-.8.2-1.1 0l9-9.5zM4.8 2.6c.3-.2.7-.2 1.1 0l10.9 6.4-3.1 3.1L4.8 2.6z"/></svg>
              <div className="t"><small>GET IT ON</small><span>Google Play</span></div>
            </a>
            <span className="cs-store soon" role="link" aria-disabled="true" aria-label="CarSpend on the App Store — coming soon">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16.4 12.7c0-2.2 1.8-3.3 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.4 0-2.8.8-3.5 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.5 2.2 2.6 2.1 1-.04 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.6 1.1-.02 1.8-1 2.5-2 .8-1.2 1.1-2.3 1.1-2.3-.02-.01-2.1-.8-2.1-3.2zM14.3 6.1c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6 1 .07 1.9-.5 2.5-1.2z"/></svg>
              <div className="t"><small>DOWNLOAD ON THE</small><span>App Store</span></div>
            </span>
          </div>
        </div>
      </div>
      <style>{`
        .cs-feats{list-style:none; padding:0; margin:0; display:grid; grid-template-columns:1fr 1fr; gap:8px 20px}
        @media (max-width:520px){ .cs-feats{grid-template-columns:1fr} }
        .cs-stores{display:flex; gap:10px; flex-wrap:wrap; margin-top:4px}
        .cs-store{display:inline-flex; align-items:center; gap:10px; text-decoration:none;
          border:1px solid var(--edge); border-radius:10px; padding:8px 14px; background:var(--panel2); color:var(--cream)}
        .cs-store:hover{border-color:var(--amber)}
        .cs-store svg{width:22px; height:22px; fill:currentColor; flex-shrink:0}
        .cs-store .t{display:flex; flex-direction:column; line-height:1.15; text-align:left; font-family:var(--mono)}
        .cs-store .t small{font-size:8px; letter-spacing:1.5px; color:var(--muted)}
        .cs-store .t span{font-size:14px; font-weight:700}
        .cs-store.soon{position:relative; opacity:.5; filter:grayscale(1); cursor:not-allowed}
        .cs-store.soon:hover{border-color:var(--edge)}
        .cs-store.soon::after{content:"Coming soon"; position:absolute; left:50%; bottom:calc(100% + 10px);
          transform:translateX(-50%) translateY(4px); background:var(--panel2); color:var(--cream); border:1px solid var(--edge);
          font-family:var(--mono); font-size:10px; letter-spacing:1.6px; text-transform:uppercase; padding:7px 11px;
          border-radius:8px; white-space:nowrap; opacity:0; pointer-events:none; transition:opacity .15s, transform .15s; z-index:60}
        .cs-store.soon:hover::after{opacity:1; transform:translateX(-50%) translateY(0)}
      `}</style>
    </Modal>
  );
}
